import type { GovernedBiContext } from "@/lib/governedBiRetrieval";
import type {
  CoachSessionPrepAgreementRecord,
  CoachSessionPrepBehavioralObservation,
  CoachSessionPrepLatestGrowthRecord,
} from "@/hooks/useCoachSessionPrepData";
import { buildCoachVeeraBehavioralReadModel } from "@/lib/behavior/coachVeeraBehavioralReadModel";
import type { LatestBehavioralPattern } from "@/lib/behavioralPatternLedger";
import type { GraphSnapshot } from "../../../../supabase/functions/_shared/contextGraph";
import type {
  BehavioralThemeV2,
  ObservationClusterV2,
  ObservationV2,
} from "../../../../supabase/functions/_shared/bi-v2/contracts";

export type CoachSessionPrepCanonicalBehaviorData = {
  governedBi?: GovernedBiContext;
  biThemes: BehavioralThemeV2[];
  biObservations?: ObservationV2[];
  biClusters: ObservationClusterV2[];
  contextGraph: GraphSnapshot | null;
  contextGraphEdges?: Array<{
    id: string;
    leader_id: string;
    from_node_id: string;
    to_node_id: string;
    edge_type: string;
    metadata: Record<string, unknown> | null;
    created_at: string;
  }>;
  compatibilitySummary: LatestBehavioralPattern | null;
};

const FALLBACK_MOMENT =
  "Coach Veera needs more shared evidence before naming a stable pattern here.";
const MAX_AGREEMENT_PATTERN_LENGTH = 72;
const MAX_AGREEMENT_PATTERN_WORDS = 8;

function lowerFirst(value: string): string {
  return value.charAt(0).toLowerCase() + value.slice(1);
}

function normalizeNextSignal(value: string): string {
  const trimmed = value.trim().replace(/[.]+$/, "");
  const withoutPrefix = trimmed.replace(/^check whether\s+/i, "");
  return lowerFirst(withoutPrefix);
}

function normalizeAgreementPatternLabel(value: string): string | null {
  const trimmed = value.trim().replace(/\s+/g, " ").replace(/[.]+$/, "");
  if (!trimmed) return null;

  const wordCount = trimmed.split(/\s+/).length;
  const looksLikeSentence = /[,:;!?]/.test(trimmed) || /^the conversation\b/i.test(trimmed);
  if (
    looksLikeSentence
    || trimmed.length > MAX_AGREEMENT_PATTERN_LENGTH
    || wordCount > MAX_AGREEMENT_PATTERN_WORDS
  ) {
    return null;
  }

  return trimmed;
}

function buildContinuityPattern(agreementRecord: CoachSessionPrepAgreementRecord | null | undefined): string | null {
  const primaryAgreement = agreementRecord?.namedAgreements[0]?.trim();
  if (!primaryAgreement) return null;
  const normalizedAgreement = normalizeAgreementPatternLabel(primaryAgreement);
  if (!normalizedAgreement) return "Stay with the current agreement.";
  return `Stay with the current agreement: ${normalizedAgreement}.`;
}

function buildReflection(params: {
  latestCoachGrowthRecord: CoachSessionPrepLatestGrowthRecord;
  contradictionReason: string | null;
}): string | null {
  const nextSignal = params.latestCoachGrowthRecord?.nextConversationSignals[0]?.trim() || null;
  if (nextSignal) {
    return `One thing to notice next session is whether ${normalizeNextSignal(nextSignal)}.`;
  }
  if (params.contradictionReason) {
    return params.contradictionReason;
  }
  return null;
}

function resolveCreatedAt(params: {
  canonicalBehavior: CoachSessionPrepCanonicalBehaviorData;
  behaviorKey: string;
}): string | null {
  return params.canonicalBehavior.biThemes.find((theme) => theme.theme_name === params.behaviorKey)?.last_seen_at
    ?? params.canonicalBehavior.contextGraph?.recentNodes[0]?.created_at
    ?? null;
}

function hasStrongCanonicalThemeSupport(params: {
  canonicalBehavior: CoachSessionPrepCanonicalBehaviorData;
  behaviorKey: string;
}): boolean {
  const theme = params.canonicalBehavior.biThemes.find((candidate) => candidate.theme_name === params.behaviorKey);
  if (!theme) return false;

  const relatedClusters = params.canonicalBehavior.biClusters.filter((cluster) => cluster.behavior_theme === params.behaviorKey);
  const strongestClusterArtifactSupport = relatedClusters.reduce(
    (max, cluster) => Math.max(max, cluster.supporting_artifact_count),
    0,
  );
  const strongestClusterMeetingSupport = relatedClusters.reduce(
    (max, cluster) => Math.max(max, cluster.supporting_meeting_count),
    0,
  );

  return (
    theme.supporting_artifact_count >= 2
    || theme.supporting_meeting_count >= 2
    || theme.occurrence_count >= 2
    || strongestClusterArtifactSupport >= 2
    || strongestClusterMeetingSupport >= 2
  );
}

export function buildCoachSessionPrepBehavioralObservation(params: {
  canonicalBehavior: CoachSessionPrepCanonicalBehaviorData;
  agreementRecord: CoachSessionPrepAgreementRecord;
  latestCoachGrowthRecord: CoachSessionPrepLatestGrowthRecord;
}): CoachSessionPrepBehavioralObservation {
  const readModel = buildCoachVeeraBehavioralReadModel({
    biThemes: params.canonicalBehavior.biThemes,
    biClusters: params.canonicalBehavior.biClusters,
    contextGraph: params.canonicalBehavior.contextGraph,
    compatibilitySummary: params.canonicalBehavior.compatibilitySummary,
  });
  const pattern = buildContinuityPattern(params.agreementRecord);
  const contradictionReason = readModel.contradictions[0]?.reason ?? null;
  const reflection = buildReflection({
    latestCoachGrowthRecord: params.latestCoachGrowthRecord,
    contradictionReason,
  });
  const hasStrongCanonicalSupport = readModel.primaryObservation.source === "bi_v2"
    ? hasStrongCanonicalThemeSupport({
        canonicalBehavior: params.canonicalBehavior,
        behaviorKey: readModel.primaryObservation.behaviorKey,
      })
    : false;

  if (readModel.primaryObservation.source !== "bi_v2" || (readModel.sourceSummary.thinEvidence && !hasStrongCanonicalSupport)) {
    return {
      source: null,
      created_at: null,
      moment: FALLBACK_MOMENT,
      pattern,
      reflection,
      isFallback: true,
    };
  }

  return {
    source: "Coach Veera",
    created_at: resolveCreatedAt({
      canonicalBehavior: params.canonicalBehavior,
      behaviorKey: readModel.primaryObservation.behaviorKey,
    }),
    moment: `The canonical behavioral read right now is ${readModel.primaryObservation.label}.`,
    pattern: pattern ?? readModel.supportingSignals[0] ?? null,
    reflection,
    isFallback: false,
  };
}
